import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, Trash2, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

async function downloadData(setBusy) {
  setBusy(true);
  try {
    const { data } = await api.get("/profile/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `itzcouple-meine-daten-${new Date().toISOString().slice(0, 10)}.json` });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Deine Daten wurden heruntergeladen");
  } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
}

const REASONS = ["Ich nutze die App zu selten", "Zu viele Benachrichtigungen", "Ich finde mich nicht zurecht", "Mir fehlen Funktionen", "Probleme mit anderen Mitgliedern", "Datenschutz-Bedenken", "Ich habe ein neues Konto", "Etwas anderes"];

const Farewell = ({ reason, setReason, note, setNote }) => (
  <div className="space-y-2" data-testid="delete-account-reason-box">
    <p className="text-sm font-bold text-slate-700">Magst du uns verraten, warum du gehst? <span className="font-normal text-slate-400">(freiwillig)</span></p>
    <div className="flex flex-wrap gap-1.5">
      {REASONS.map((r, i) => (
        <button key={r} type="button" onClick={() => setReason(reason === r ? "" : r)} data-testid={`delete-reason-${i}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${reason === r ? "bg-rose-500 border-rose-500 text-white" : "border-slate-200 text-slate-600 hover:border-rose-300"}`}>{r}</button>
      ))}
    </div>
    <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2} placeholder="Möchtest du uns noch etwas sagen?" data-testid="delete-account-note"
      className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200 resize-none" />
  </div>
);

function DeleteDialog({ google, onClose }) {
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = val.trim().toUpperCase() === "LÖSCHEN" && (google || pw.length > 0);
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile/delete", { ...(google ? {} : { password: pw }), confirm: val, reason: reason || null, note: note.trim() || null });
      toast.success(`Dein Konto wird am ${new Date(data.delete_on).toLocaleDateString("de-DE")} endgültig gelöscht. Melde dich bis dahin an, um es zurückzuholen 💔`, { duration: 10000 });
      navigate("/", { replace: true });
      window.location.reload();
    } catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  const cls = "w-full h-12 px-4 rounded-2xl border border-red-200 text-base focus:outline-none focus:ring-2 focus:ring-red-300";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto rounded-3xl" data-testid="delete-account-dialog">
        <DialogTitle className="font-bold text-red-600">Konto löschen?</DialogTitle>
        <DialogDescription className="text-sm text-slate-600">Dein Konto wird sofort deaktiviert und ist für niemanden mehr sichtbar. Nach <b>14 Tagen</b> werden Profil, Beiträge, Storys, Kommentare, Bilder, Nachrichten, Follower, Punkte, Haustier-Daten und Einlösungen endgültig gelöscht. Hast du es dir anders überlegt? Melde dich einfach innerhalb der 14 Tage wieder an – dann ist alles wie vorher. Lade vorher deine Daten herunter, wenn du sie behalten möchtest.</DialogDescription>
        <Farewell reason={reason} setReason={setReason} note={note} setNote={setNote} />
        {!google && <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" placeholder="Dein Passwort" data-testid="delete-account-password" className={cls} />}
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)} autoComplete="off" placeholder="Tippe LÖSCHEN zur Bestätigung" data-testid="delete-account-input" className={cls} />
        <div className="flex gap-2">
          <button onClick={onClose} data-testid="delete-account-cancel" className="flex-1 h-11 rounded-full bg-slate-100 font-semibold text-sm text-slate-600">Abbrechen</button>
          <button onClick={del} disabled={!ok || busy} data-testid="delete-account-confirm" className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Konto löschen
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogoutAll() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Auf allen Geräten abmelden? Du musst dich danach überall neu anmelden.")) return;
    setBusy(true);
    try { await api.post("/auth/logout-all"); toast.success("Du wurdest auf allen Geräten abgemeldet"); setUser(false); navigate("/login", { replace: true }); }
    catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid="logout-all-btn" className="w-full h-11 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm flex items-center justify-center gap-2 transition-colors">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Auf allen Geräten abmelden
    </button>
  );
}

export function AccountPrivacy() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const isAdmin = user?.role === "admin";
  return (
    <div className="bg-white rounded-3xl p-6 border border-pink-100 space-y-3" data-testid="account-privacy">
      <h3 className="font-bold text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rose-500" /> Meine Daten & Konto</h3>
      <p className="text-sm text-slate-500">Lade alle über dich gespeicherten Daten als Datei herunter oder lösche dein Konto.</p>
      <button onClick={() => downloadData(setBusy)} disabled={busy} data-testid="export-data-btn" className="w-full h-11 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-sm flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Meine Daten herunterladen
      </button>
      <LogoutAll />
      {isAdmin
        ? <p className="text-xs text-slate-400" data-testid="delete-account-admin-note">Admin-Konten können nicht selbst gelöscht werden.</p>
        : <button onClick={() => setDel(true)} data-testid="delete-account-btn" className="w-full h-11 rounded-full border border-red-200 text-red-600 hover:bg-red-50 font-bold text-sm flex items-center justify-center gap-2 transition-colors"><Trash2 className="w-4 h-4" /> Konto löschen</button>}
      {del && <DeleteDialog google={!user.has_password} onClose={() => setDel(false)} />}
    </div>
  );
}
